/**
 * CorrectionsTab — Financials → Corrections: audited, reversible restatements
 * of ChannelMeter months (Earnings Corrections).
 *
 * The list is readable with Financials access; creating, applying, reverting
 * and recording settlement need "Financials · Correct Earnings"
 * (`financials.corrections`, never implied by Admin) — the server enforces it
 * and this tab only hides the buttons. ChannelMeter, its creator emails and
 * the payout sheets never change.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, Spinner, StatusPill, Toast, tableStyles } from '../../ui';
import type { ToastKind } from '../../ui';
import {
  listEarningsCorrections,
  CorrectionListItem,
  CorrectionStatus,
  EarningsCorrection,
} from '../../../services/earningsCorrectionsApi';
import {
  correctionErrorMessage,
  formatSignedCents,
  isMonth,
  KIND_LABEL,
  monthRangeLabel,
  SETTLEMENT_META,
  STATUS_META,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';
import CorrectionDetail from './CorrectionDetail';
import NewCorrectionModal from './NewCorrectionModal';

type StatusFilter = CorrectionStatus | 'all';

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Drafts' },
  { value: 'applied', label: 'Applied' },
  { value: 'reverted', label: 'Reverted' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'discarded', label: 'Discarded' },
];

/** Applied, and the difference has not been paid or waived yet. */
export const isUnsettled = (c: Pick<CorrectionListItem, 'status' | 'settlement'>): boolean =>
  c.status === 'applied' && c.settlement.status === 'pending';

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

interface CorrectionsTabProps {
  /** Holds `financials.corrections` (create / apply / revert / settle). */
  canCorrect: boolean;
  /** The open correction (the `?correction=` deep link, owned by the dashboard). */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Month filter to start from (the Earnings banner and the Pipeline notice link here). */
  initialMonth?: string | null;
}

const CorrectionsTab: React.FC<CorrectionsTabProps> = ({ canCorrect, selectedId, onSelect, initialMonth }) => {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [month, setMonth] = useState<string>(initialMonth && isMonth(initialMonth) ? initialMonth : '');
  const [unsettledOnly, setUnsettledOnly] = useState(false);
  const [items, setItems] = useState<CorrectionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);

  useEffect(() => {
    if (initialMonth && isMonth(initialMonth)) setMonth(initialMonth);
  }, [initialMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { corrections } = await listEarningsCorrections({
        status: status === 'all' ? undefined : status,
        month: month || undefined,
      });
      setItems(corrections);
    } catch (err) {
      setError(correctionErrorMessage(err, 'Failed to load corrections.'));
    } finally {
      setLoading(false);
    }
  }, [status, month]);

  useEffect(() => {
    if (!selectedId) load();
  }, [load, selectedId]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const visible = useMemo(() => (unsettledOnly ? items.filter(isUnsettled) : items), [items, unsettledOnly]);
  const unsettledCount = useMemo(() => items.filter(isUnsettled).length, [items]);

  const handleCreated = (correction: EarningsCorrection) => {
    setNewOpen(false);
    setToast({ kind: 'success', title: 'Draft saved', message: 'Review it, then apply it when you’re ready.' });
    onSelect(correction.id);
  };

  const toastEl = toast && (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 50 }}>
      <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={() => setToast(null)} />
    </div>
  );

  if (selectedId) {
    return (
      <>
        {toastEl}
        <CorrectionDetail
          id={selectedId}
          canCorrect={canCorrect}
          onBack={() => onSelect(null)}
          onOpen={onSelect}
          onNotify={setToast}
        />
      </>
    );
  }

  return (
    <div style={cs.section}>
      {toastEl}
      <div style={cs.header}>
        <div>
          <h2 style={cs.title}>Earnings Corrections</h2>
          <p style={cs.subtitle}>
            Restate a ChannelMeter month in Culture Genius, e.g. after a GAM re-upload. Applied corrections show on the
            Earnings tab, in chat and on client report pages (marked “Updated”). ChannelMeter, its creator emails and the
            payout sheets never change, so any difference is settled outside ChannelMeter.
          </p>
        </div>
        {canCorrect && (
          <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
            New correction
          </Button>
        )}
      </div>

      <div style={{ ...cs.controls, marginBottom: 12 }}>
        <select
          aria-label="Status"
          style={cs.select}
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          aria-label="Month"
          type="month"
          style={{ ...cs.select, width: 160 }}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        {month && (
          <Button variant="subtle" size="sm" onClick={() => setMonth('')}>
            All months
          </Button>
        )}
        <label style={cs.checkboxLabel}>
          <input type="checkbox" checked={unsettledOnly} onChange={(e) => setUnsettledOnly(e.target.checked)} />
          Unsettled only{unsettledCount ? ` (${unsettledCount})` : ''}
        </label>
        <Button variant="subtle" size="sm" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      {!canCorrect && (
        <div style={{ ...noticeStyle('neutral'), marginBottom: 12 }}>
          You can review corrections. Creating or applying one needs the “Financials · Correct Earnings” permission.
        </div>
      )}
      {error && (
        <div style={{ ...noticeStyle('danger'), marginBottom: 12 }} role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div style={cs.loadingRow}>
          <Spinner label="Loading corrections…" />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          brand={false}
          maxWidth={480}
          title={items.length ? 'Nothing matches these filters' : 'No corrections yet'}
          subtitle={
            items.length
              ? 'Clear the filters to see every correction.'
              : 'Every month shows exactly what ChannelMeter paid. Start a correction to restate one.'
          }
        />
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Correction</th>
                <th style={tableStyles.th}>Status</th>
                <th style={tableStyles.th}>Months</th>
                <th style={tableStyles.thNum}>Lines</th>
                <th style={tableStyles.thNum} title="Corrected − ChannelMeter creator earnings">
                  Net change ⓘ
                </th>
                <th style={tableStyles.th}>Created</th>
                <th style={tableStyles.th}>Applied</th>
                <th style={tableStyles.th}>Settlement</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => {
                const net = c.summary.netDeltaCents;
                return (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelect(c.id)}
                    onMouseEnter={(ev) => {
                      ev.currentTarget.style.background = tableStyles.rowHoverBackground;
                    }}
                    onMouseLeave={(ev) => {
                      ev.currentTarget.style.background = '';
                    }}
                  >
                    <td style={tableStyles.td}>
                      <button
                        type="button"
                        style={{ ...cs.linkButton, color: 'var(--fg-1)', textDecoration: 'none', fontWeight: 600, textAlign: 'left' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(c.id);
                        }}
                      >
                        {c.title}
                      </button>
                      <div style={cs.small}>
                        {KIND_LABEL[c.kind]} · {c.channelIds.length} channel{c.channelIds.length === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td style={tableStyles.td}>
                      <StatusPill variant={STATUS_META[c.status].variant}>{STATUS_META[c.status].label}</StatusPill>
                    </td>
                    <td style={tableStyles.td}>{monthRangeLabel(c.months)}</td>
                    <td style={tableStyles.tdNum}>{c.summary.lineCount}</td>
                    <td
                      style={{ ...tableStyles.tdNum, fontWeight: 600, color: deltaColor(net.total) }}
                      title={`Direct Sold ${formatSignedCents(net.directSold)} · AdSense ${formatSignedCents(net.adSense)}`}
                    >
                      {formatSignedCents(net.total)}
                    </td>
                    <td style={tableStyles.td}>
                      <div>{fmtDate(c.createdAt)}</div>
                      <div style={cs.small}>{c.createdBy.email}</div>
                    </td>
                    <td style={tableStyles.td}>
                      {c.appliedAt ? (
                        <>
                          <div>{fmtDate(c.appliedAt)}</div>
                          <div style={cs.small}>{c.appliedBy?.email}</div>
                        </>
                      ) : (
                        <span style={cs.muted}>—</span>
                      )}
                    </td>
                    <td style={tableStyles.td}>
                      {c.status === 'applied' || c.status === 'draft' ? (
                        <StatusPill variant={SETTLEMENT_META[c.settlement.status].variant} noDot>
                          {SETTLEMENT_META[c.settlement.status].label}
                        </StatusPill>
                      ) : (
                        <span style={cs.muted}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && <NewCorrectionModal onClose={() => setNewOpen(false)} onCreated={handleCreated} />}
    </div>
  );
};

export default CorrectionsTab;
